import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { blogPosts } from '@/data/blogPosts';

const Blog = () => {
  const blogListSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Precision Vapour Blasting Blog',
    url: 'https://precisionvapour.co.nz/blog',
    description:
      'Guides and project notes on vapour blasting, alloy restoration and surface cleaning in Christchurch and Canterbury.',
    publisher: {
      '@type': 'Organization',
      name: 'Precision Vapour Blasting',
      logo: {
        '@type': 'ImageObject',
        url: 'https://precisionvapour.co.nz/og-image.png',
      },
    },
    blogPost: blogPosts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      author: { '@type': 'Organization', name: p.author },
      url: `https://precisionvapour.co.nz/blog/${p.slug}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://precisionvapour.co.nz/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://precisionvapour.co.nz/blog' },
    ],
  };

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>Vapour Blasting Blog | Tips & Guides | Precision Vapour Blasting Christchurch</title>
        <meta
          name="description"
          content="Guides, tips and project notes from Precision Vapour Blasting Christchurch — wet blasting alloy parts, motorcycle engine restoration, marine corrosion removal."
        />
        <link rel="canonical" href="https://precisionvapour.co.nz/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Vapour Blasting Blog | Precision Vapour Blasting Christchurch" />
        <meta property="og:description" content="Guides, tips and project notes from Precision Vapour Blasting Christchurch." />
        <meta property="og:url" content="https://precisionvapour.co.nz/blog" />
        <script type="application/ld+json">{JSON.stringify(blogListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />
      <main className="flex-1 pt-32 pb-20 bg-background">
        <div className="container-wide mx-auto max-w-5xl px-4 md:px-8">
          <header className="text-center mb-16">
            <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
              Blog
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
              Vapour Blasting <span className="gradient-text">Insights</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Practical guides on surface restoration, alloy cleaning and project tips from our
              Christchurch workshop.
            </p>
          </header>

          <div className="grid gap-8 md:grid-cols-2">
            {blogPosts.map((post) => (
              <article
                key={post.slug}
                className="glass-card rounded-xl p-8 flex flex-col hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(post.date).toLocaleDateString('en-NZ', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {post.readMinutes} min read
                  </span>
                </div>
                <h2 className="font-display text-2xl font-bold mb-3 text-foreground">
                  <Link to={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                    {post.title}
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 flex-1">{post.description}</p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                >
                  Read article <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
